import React, { useState, useMemo, useEffect } from 'react';
import {
    Paper, Typography, Stack, Switch, FormControlLabel, Button, Box
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { format, addDays } from 'date-fns';
import { useForecast } from '../../hooks/useForecast';

const ForecastCashFlow = () => {
    const [showTentative, setShowTentative] = useState(true);
    const [rangeDays, setRangeDays] = useState(90);
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useMemo(() => {
        const now = new Date();
        return {
            startDate: format(addDays(now, -7), 'yyyy-MM-dd'),
            endDate: format(addDays(now, rangeDays), 'yyyy-MM-dd'),
        };
    }, [rangeDays]);

    const { data, loading } = useForecast(showTentative, startDate, endDate);
    const hasSavings = useMemo(() => data.some((p) => Math.abs(p.savings) > 0), [data]);

    useEffect(() => {
        if (!loading) {
            console.log('Forecast sample (last day):', data?.[data.length - 1]);
        }
    }, [data, loading]);

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold">
                📈 Forecast & Cash Flow
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Forecast includes recurring and manually scheduled future transactions by default.
                Toggle "Include Tentative" to preview planned-but-unconfirmed entries.
            </Typography>

            <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={<Switch checked={showTentative} onChange={() => setShowTentative(!showTentative)} />}
                        label="Include Tentative"
                    />
                    <Typography variant="body2">Timeframe:</Typography>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {[30, 60, 90].map((days) => (
                        <Button
                            key={days}
                            size="small"
                            variant={rangeDays === days ? 'contained' : 'outlined'}
                            onClick={() => setRangeDays(days)}
                        >
                            {days} days
                        </Button>
                    ))}
                </Box>
            </Stack>

            {loading ? (
                <Typography sx={{ mt: 2 }}>Loading forecast...</Typography>
            ) : (
                <>
                    {!hasSavings && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Note: No savings transactions in this forecast window.
                        </Typography>
                    )}
                    <ResponsiveContainer width="100%" height={isPhone ? 330 : 300}>
                        <LineChart data={data} margin={{ top: isPhone ? 16 : 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" minTickGap={isPhone ? 20 : 10} />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="balance" stroke="#3f51b5" strokeWidth={2} name="Available Balance" />
                            <Line type="monotone" dataKey="wealth" stroke="#4caf50" strokeWidth={2} name="Net Worth" />
                            <Line type="monotone" dataKey="income" stroke="#2e7d32" strokeWidth={2} name="Income" />
                            <Line type="monotone" dataKey="expense" stroke="#c62828" strokeWidth={2} dot={false} name="Expense" />
                            <Line type="monotone" dataKey="savings" stroke="#00838f" strokeWidth={2} dot={false} name="Savings" />

                            <Legend
                                verticalAlign="top"
                                align="left"
                                height={isPhone ? 64 : 36}
                                wrapperStyle={{ fontSize: isPhone ? 11 : 12 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </>

            )}
        </Paper>
    );
};

export default ForecastCashFlow;
